<template>
  <div class="flex flex-col items-start" :class="fieldWidth ? `field-${fieldWidth}` : 'w-full'">
    <Listbox v-model="val" v-slot="{ open }" :disabled="disabled">
      <ListboxLabel
        v-if="!hideLabel && label"
        :for="listboxId"
        :class="small ? 'input--label--small' : 'input--label'"
      >
        {{ label }}
      </ListboxLabel>

      <div class="relative w-full">
        <ListboxButton
          :id="listboxId"
          ref="buttonRef"
          :class="[
            small
              ? 'input--select--small select-field__button'
              : 'input--select select-field__button',
            'w-full text-left',
            !hasValue ? 'select-field__button--placeholder' : '',
          ]"
          @click="updateMenuPosition"
          @keydown="updateMenuPosition"
        >
          <span class="select-field__value">{{ displayText }}</span>
          <span class="select-field__chevron" aria-hidden="true">
            <ChevronUpDownIcon class="h-4 w-4 text-gray-400" />
          </span>
        </ListboxButton>

        <Teleport to="body">
          <transition
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <ListboxOptions v-if="open" class="select-field__menu" :style="menuStyle">
              <ListboxOption
                v-for="(option, index) in selectOptions"
                :key="`${option.value}-${index}`"
                v-slot="{ active, selected }"
                :value="option.value"
                as="template"
              >
                <li
                  :class="[
                    'select-field__option',
                    active ? 'select-field__option--active' : '',
                    selected ? 'select-field__option--selected' : '',
                  ]"
                >
                  <span class="block truncate">{{ option.text }}</span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </Teleport>
      </div>
    </Listbox>

    <ul v-if="errors.length" class="input--errors">
      <li
        v-for="(error, index) in errors"
        :key="index"
        v-text="error.message"
        class="input--error"
      />
    </ul>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref } from 'vue'
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue'
import { ChevronUpDownIcon } from '@heroicons/vue/20/solid'

let selectFieldCounter = 0

const MENU_MAX_HEIGHT = 208

export default {
  name: 'SelectField',
  components: {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    ChevronUpDownIcon,
  },
  props: {
    label: {
      type: String,
    },
    selectOptions: {
      type: Array,
      required: true,
    },
    value: {
      type: [String, Number],
      required: true,
      default: '',
    },
    errors: {
      type: Array,
      required: true,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fieldWidth: {
      type: String,
      default: '',
    },
  },
  emits: ['blur', 'focus', 'input', 'update:value'],

  setup(props, context) {
    const listboxId = `select-field-${++selectFieldCounter}`
    const buttonRef = ref(null)
    const menuStyle = ref({})

    const val = computed({
      get: () => props.value,
      set: (value) => context.emit('update:value', value),
    })

    const hasValue = computed(
      () => props.value !== '' && props.value !== null && props.value !== undefined,
    )

    const displayText = computed(() => {
      if (!hasValue.value) {
        return props.label ? `Select ${props.label}` : 'Select'
      }
      const match = props.selectOptions.find(
        (option) => String(option.value) === String(props.value),
      )
      return match?.text ?? props.value
    })

    function getButtonElement() {
      const target = buttonRef.value
      return target?.$el ?? target
    }

    function updateMenuPosition() {
      const button = getButtonElement()
      if (!button) return

      const rect = button.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom - 12
      const maxHeight = Math.min(MENU_MAX_HEIGHT, Math.max(spaceBelow, 120))

      menuStyle.value = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${maxHeight}px`,
      }
    }

    function handleViewportChange() {
      if (getButtonElement()) {
        updateMenuPosition()
      }
    }

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    })

    return {
      listboxId,
      buttonRef,
      menuStyle,
      val,
      hasValue,
      displayText,
      updateMenuPosition,
    }
  },
}
</script>
